<template>
  <BaseModal modal-class="share-diet-with-patient-modal" @close="close">
    <template #title>Udostępnij jadłospis pacjentowi</template>

    <div class="share-diet-content">
      <div class="share-diet-description">
        Jadłospis wyświetli się pacjentowi w aplikacji mobilnej Kcalmar.
      </div>

      <div class="share-diet-date-row">
        <div class="share-diet-date-picker-wrap">
          <DatePicker
            v-model="selectedStartDate"
            :disabled="allowPatientToChooseStartDate || isFetchingPublishDates"
            :disabled-dates="disabledDatesFunction"
            :get-date-status="getDateStatus"
            :selection-span-in-days="selectionSpanInDays"
          />
          <div v-if="isFetchingPublishDates" class="share-diet-loader-overlay">
            <BaseLoader class="base-loader--size-24" />
          </div>
          <CalendarLegend class="share-diet-legend" />
        </div>
      </div>

      <div class="share-diet-switch-row">
        <BaseSwitch
          :value="allowPatientToChooseStartDate"
          @click="allowPatientToChooseStartDate = $event"
        />
        <span class="share-diet-switch-label">Pozwól pacjentowi wybrać datę startu</span>
      </div>
    </div>
    <template #actions>
      <StyledButton variant="outline" @click.once="close">Anuluj</StyledButton>
      <StyledButton :disabled="!isFormValid || sending" @click.once="onSubmit">
        Udostępnij
      </StyledButton>
      <BaseLoader v-if="sending" class="base-loader--size-12" />
    </template>
  </BaseModal>
</template>

<script lang="ts" setup>
  import { G } from '@mobily/ts-belt'
  import { useQuery } from '@tanstack/vue-query'
  import { format, parse } from 'date-fns'
  import { computed, onBeforeUnmount, ref } from 'vue'

  import BaseLoader from '@/components/BaseLoader.vue'
  import BaseModal from '@/components/BaseModal.vue'
  import BaseSwitch from '@/components/BaseSwitch.vue'
  import { DatePicker } from '@/components/DatePicker'
  import { StyledButton } from '@/components/StyledButton'
  import { CalendarLegend } from '@/pages/diets/components/CalendarLegend'
  import { dietsService } from '@/services/dietsService'
  import { useGlobalStore } from '@/store/globalStore'
  import {
    getMealPlanDateStatuses,
    type MealPlanDateStatus,
    toMealPlanDateKey,
  } from '@/utils/getMealPlanDateStatuses'
  import { reportError } from '@/utils/reportError'

  type PublishDatesResponse = { dates: { date: number; status: MealPlanDateStatus }[] }

  const props = defineProps({
    daysCount: {
      required: true,
      type: Number,
    },
    dietId: {
      required: true,
      type: Number,
    },
    patientId: {
      required: true,
      type: Number,
    },
    startDate: {
      default: undefined,
      required: false,
      type: String,
    },
  })

  const emit = defineEmits(['close', 'success'])

  const globalStore = useGlobalStore()
  const sending = ref(false)

  // Initialize startDate from prop or default to today
  const getInitialStartDate = (): Date | undefined => {
    if (props.startDate) {
      // Try parsing as 'dd-MM-yyyy' format first (diet format)
      if (props.startDate.includes('-') && props.startDate.split('-').length === 3) {
        try {
          const date = parse(props.startDate, 'dd-MM-yyyy', new Date())
          if (!isNaN(date.getTime())) {
            return date
          }
        } catch {
          // Fall through to try other formats
        }
      }
      // Try parsing as ISO date string
      const date = new Date(props.startDate)
      if (!isNaN(date.getTime())) {
        return date
      }
    }
    // Default to empty value if no startDate provided
    return undefined
  }

  const selectedStartDate = ref<Date | undefined>(getInitialStartDate())
  const allowPatientToChooseStartDate = ref(false)
  const selectionSpanInDays = computed(() => props.daysCount)

  const isFormValid = computed(
    () => allowPatientToChooseStartDate.value || G.isNotNullable(selectedStartDate.value),
  )

  const { data: publishDatesData, isFetching: isFetchingPublishDates } =
    useQuery<PublishDatesResponse>({
      enabled: computed(() => !!props.dietId),
      queryFn: async (): Promise<PublishDatesResponse> => {
        try {
          const response = await dietsService.getBlockedDates(props.dietId)
          return { dates: response.data.dates }
        } catch (err: unknown) {
          reportError(err, 'Error while fetching blocked dates', {
            dietId: props.dietId,
          })
          return { dates: [] }
        }
      },
      queryKey: computed(() => ['blockedDates', props.dietId]),
      retry: false,
    })

  const dateStatusByKey = computed(() =>
    getMealPlanDateStatuses(publishDatesData.value?.dates, selectionSpanInDays.value),
  )

  const getDateStatus = (date: Date): MealPlanDateStatus | null =>
    dateStatusByKey.value.get(toMealPlanDateKey(date)) ?? null

  const disabledDatesFunction = (date: Date): boolean =>
    dateStatusByKey.value.has(toMealPlanDateKey(date))


  const close = () => {
    selectedStartDate.value = getInitialStartDate()
    allowPatientToChooseStartDate.value = false
    emit('close')
  }

  onBeforeUnmount(() => {
    selectedStartDate.value = getInitialStartDate()
    allowPatientToChooseStartDate.value = false
  })

  const onSubmit = async () => {
    try {
      sending.value = true
      const startDateString =
        !allowPatientToChooseStartDate.value && G.isNotNullable(selectedStartDate.value)
          ? format(selectedStartDate.value, 'yyyy-MM-dd')
          : undefined

      await dietsService.publishDiet(props.dietId, startDateString)

      globalStore.createMessage({
        title: 'Jadłospis został udostępniony w aplikacji',
      })

      emit('success')
      close()
    } catch (err) {
      reportError(err, 'Error while publishing diet', {
        allowPatientToChooseStartDate: allowPatientToChooseStartDate.value,
        dietId: props.dietId,
        patientId: props.patientId,
        startDate: selectedStartDate.value,
      })
      globalStore.createMessage({
        title: 'Nie udało się udostępnić jadłospisu. Spróbuj ponownie.',
        type: 'error',
      })
    } finally {
      sending.value = false
    }
  }
</script>

<style>
  .share-diet-with-patient-modal {
    width: 550px;
    overflow-y: auto;
  }
</style>

<style scoped>
  .share-diet-content {
    display: flex;
    flex-direction: column;
    gap: 20px;
  }

  .share-diet-description {
    font-size: 14px;
    line-height: 1.5;
    color: var(--color-tertiary-text);
  }

  .share-diet-date-row {
    display: flex;
    flex-direction: column;
    gap: 8px;
    align-items: center;
  }

  .share-diet-date-picker-wrap {
    position: relative;
    display: flex;
    flex-direction: column;
    gap: 8px;
  }

  .share-diet-switch-row {
    display: flex;
    gap: 12px;
    align-items: center;
  }

  .share-diet-switch-label {
    font-size: 14px;
    color: var(--color-primary-text);
  }

  .share-diet-legend {
    align-self: flex-end;
    margin-right: 4px;
  }

  .share-diet-loader-overlay {
    position: absolute;
    inset: 0;
    z-index: 1;
    display: flex;
    align-items: center;
    justify-content: center;
    background-color: rgb(255 255 255 / 80%);
    border-radius: 8px;
  }
</style>
